import { Box, IconButton, styled, Tooltip } from '@mui/material';
import { ThemeButton } from 'DesignSystem';
import RiskLevelBadge from 'DesignSystem/DataDisplay/RiskLevelBadge/RiskLevelBadge';
import Icon from 'DesignSystem/Primitives/Icon/Icon';
import ThemeTypography from 'DesignSystem/Primitives/Typography/ThemeTypography';
import React, { FC, MouseEvent } from 'react';
import { Booleanish, Tbooleanish } from 'types/booleanish.types';
import {
  EPartnerRequestStatus,
  ERequestType,
  IPartnerRequest,
  TPartnerRequestStatus,
} from 'FeatureModules/Partner/types/partner.types';
import {
  IQuestionnaire,
  TQuestionnaireRiskLevel,
} from 'FeatureModules/Questionnaire/types/questionnaire.types';
import FlexBox from 'Components/FlexBox';
import {
  REQUEST_STATUS_CLIENT,
  REQUEST_STATUS_COLOR_MAPPING,
} from 'FeatureModules/Request/utils/requestStatus.utils';

interface IQuestionnaireItemProps {
  questionnaire: IQuestionnaire;
  riskLevel?: TQuestionnaireRiskLevel;
  requestStatus?: TPartnerRequestStatus;
  partnerName?: string;
  actionButtonsAsText?: boolean;
  onClick?: () => void;
  onEdit?: () => void;
  onDelete?: () => void;
  onReviewRequest?: () => void;
}

const ItemContainer = styled(FlexBox)<{ 'is-pending'?: Tbooleanish; clickable?: Tbooleanish }>(
  ({ theme, 'is-pending': isPending, clickable }) => ({
    position: 'relative',
    width: '100%',
    height: 68,
    borderRadius: 8,
    backgroundColor: theme.palette.common.white,
    padding: `0 ${theme.spacing(2)}`,

    ...(clickable === 'true'
      ? {
          cursor: 'pointer',
          '&:hover': { backgroundColor: `${theme.custom.colors.backgroundLight} !important` },
        }
      : {}),

    ...(isPending === 'true'
      ? {
          boxShadow: theme.shadows[4],
          '&:before': {
            content: "''",
            margin: `0 -${theme.spacing(2)}`,
            position: 'absolute',
            width: '100%',
            borderRadius: 6,
            height: '100%',
            border: `1px dashed ${theme.custom.themeColors.grayScale[40]}`,
          },
        }
      : { border: `1px solid ${theme.custom.themeColors.grayScale[20]}` }),
  })
);

const ActionsContainer = styled(FlexBox)(() => ({
  gap: 8,
  justifyContent: 'end',
}));

const Status = styled(ThemeTypography)<{ status: IPartnerRequest['requestStatus'] }>(
  ({ status }) => ({
    fontSize: 13,
    marginRight: 8,
    textTransform: 'uppercase',
    color: `${REQUEST_STATUS_COLOR_MAPPING[status]} !important`,
  })
);

const QuestionnaireItem: FC<IQuestionnaireItemProps> = ({
  questionnaire,
  partnerName,
  requestStatus,
  actionButtonsAsText,
  riskLevel,
  onEdit,
  onDelete,
  onClick,
  onReviewRequest,
}) => {
  const handleOnClick = () => {
    if (onClick) {
      onClick();
    } else if (requestStatus === EPartnerRequestStatus.PROVIDED) {
      onReviewRequest?.();
    } else {
      onEdit?.();
    }
  };

  const handleEdit = (event: MouseEvent) => {
    event.stopPropagation();
    onEdit?.();
  };

  const handleDelete = (event: MouseEvent) => {
    event.stopPropagation();
    onDelete?.();
  };

  const handleReviewRequest = (event: MouseEvent) => {
    event.stopPropagation();
    onReviewRequest?.();
  };

  return (
    <ItemContainer
      data-testid="questionnaire-item"
      clickable={Booleanish(!!onEdit || onClick)}
      is-pending={Booleanish(requestStatus === EPartnerRequestStatus.REQUESTED)}
      onClick={handleOnClick}
    >
      <Box display="flex" bgcolor="white" borderRadius="6px" padding="4px">
        <Icon
          name={
            requestStatus
              ? REQUEST_STATUS_CLIENT[ERequestType.QUESTIONNAIRE][requestStatus].iconName
              : 'document'
          }
          size="xx-large"
          color="gray-100"
        />
      </Box>

      <Box width="calc(100% - 56px)" ml={1.5}>
        <FlexBox gap={1} maxWidth="80%">
          <ThemeTypography variant="ITEM_TITLE" autoOverflow>
            {questionnaire.title}
          </ThemeTypography>

          {questionnaire.externalId && (
            <>
              <ThemeTypography variant="BODY_MEDIUM" color="GRAY_100">
                |
              </ThemeTypography>
              <ThemeTypography autoOverflow variant="BODY_MEDIUM" color="GRAY_100">
                {questionnaire.externalId}
              </ThemeTypography>
            </>
          )}

          {requestStatus && (
            <Status variant="BODY_MEDIUM" status={requestStatus} autoOverflow>
              {REQUEST_STATUS_CLIENT[ERequestType.QUESTIONNAIRE][requestStatus].statusTitle}
            </Status>
          )}

          {riskLevel && <RiskLevelBadge riskLevel={riskLevel} />}
        </FlexBox>

        <FlexBox gap={1}>
          <FlexBox gap={0.5}>
            <Icon name="tag-filled" size="small" color="gray-60" />
            <ThemeTypography variant="LABEL_INPUT">Self-assessment request</ThemeTypography>
          </FlexBox>

          <FlexBox gap={0.5}>
            <Icon name="calendar" size="small" color="gray-60" />
            <ThemeTypography autoOverflow variant="BODY_MEDIUM_BOLD" color="GRAY_80">
              {questionnaire.year}
            </ThemeTypography>
          </FlexBox>

          {partnerName && (
            <FlexBox gap={0.5}>
              <Icon name="partner" size="small" color="gray-60" />
              <ThemeTypography autoOverflow variant="BODY_MEDIUM_BOLD" color="GRAY_80">
                {partnerName}
              </ThemeTypography>
            </FlexBox>
          )}
        </FlexBox>
      </Box>

      <ActionsContainer>
        {onReviewRequest &&
          requestStatus === EPartnerRequestStatus.PROVIDED &&
          (actionButtonsAsText ? (
            <ThemeButton
              color="PRIMARY"
              startIcon={<Icon name="document-request-fulfilled" color="WHITE" />}
              onClick={event => handleReviewRequest(event as MouseEvent)}
            >
              Review request
            </ThemeButton>
          ) : (
            <Tooltip title="Review request">
              <IconButton size="small" onClick={handleReviewRequest}>
                <Icon name="document-request-fulfilled" />
              </IconButton>
            </Tooltip>
          ))}
        {onEdit && (
          <Tooltip title="Edit">
            <IconButton size="small" onClick={handleEdit}>
              <Icon name="edit" />
            </IconButton>
          </Tooltip>
        )}

        {onDelete && (
          <Tooltip title="Delete request">
            <IconButton size="small" onClick={handleDelete}>
              <Icon name="delete" />
            </IconButton>
          </Tooltip>
        )}
      </ActionsContainer>
    </ItemContainer>
  );
};

export default QuestionnaireItem;
